import { useState, useRef, useEffect, useCallback } from "react"
import { ask, getWelcome, saveReaction, startParty, partyReply, getAutopilotStatus, toggleAutopilot, getIncidents } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import MessageBubble from "@/components/MessageBubble"
import { PixelPortrait } from "@/components/AgentCard"
import { Send, PartyPopper, X, Shield, Reply } from "lucide-react"
import type { Tab } from "@/App"

interface SuggestedAction {
  action: string
  target?: string
  tier: number
  reason: string
}

interface Message {
  role: "user" | "assistant" | "system" | "party"
  content: string
  meta?: {
    backend: string
    agents: string[]
    suggestedActions?: SuggestedAction[]
    confidence?: number
    signals?: string[]
    rationale?: string
    evidence?: string[]
  }
  partyAgent?: { id: string; name: string; color: string }
}

interface Props {
  onActivity?: (agents: string[]) => void
  prefillInput?: string
  onPrefillConsumed?: () => void
  onSwitchTab?: (tab: Tab) => void
}

const SESSION_KEY = "pulse_session_id"
const SESSION_TS_KEY = "pulse_session_ts"
const SESSION_TTL_MS = 30 * 60 * 1000  // 30 minutes

// Agent names + colors for party & @mentions
const AGENTS = [
  { id: "commander", name: "Commander", color: "#facc15" },
  { id: "api", name: "Sentry", color: "#6366f1" },
  { id: "worker", name: "Forge", color: "#f59e0b" },
  { id: "redis", name: "Cache", color: "#ef4444" },
  { id: "db", name: "Vault", color: "#22c55e" },
  { id: "railway", name: "Gateway", color: "#3b82f6" },
  { id: "ui", name: "Pixel", color: "#ec4899" },
  { id: "ops", name: "Wrench", color: "#f97316" },
  { id: "moderator", name: "Shield", color: "#a855f7" },
  { id: "labs", name: "Scope", color: "#14b8a6" },
] as const

const AGENT_COLORS: Record<string, string> = Object.fromEntries(AGENTS.map(a => [a.id, a.color]))

function loadSession(): string | null {
  const id = localStorage.getItem(SESSION_KEY)
  const ts = localStorage.getItem(SESSION_TS_KEY)
  if (!id || !ts) return null
  if (Date.now() - parseInt(ts, 10) > SESSION_TTL_MS) {
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(SESSION_TS_KEY)
    return null
  }
  return id
}

function saveSession(id: string) {
  localStorage.setItem(SESSION_KEY, id)
  localStorage.setItem(SESSION_TS_KEY, Date.now().toString())
}

// ── Message persistence ──────────────────────────────────────────────────
const MESSAGES_KEY = "pulse_chat_messages"
const MESSAGES_TS_KEY = "pulse_chat_ts"

function loadStoredMessages(): Message[] | null {
  try {
    const ts = sessionStorage.getItem(MESSAGES_TS_KEY)
    if (!ts || Date.now() - parseInt(ts, 10) > SESSION_TTL_MS) {
      sessionStorage.removeItem(MESSAGES_KEY)
      sessionStorage.removeItem(MESSAGES_TS_KEY)
      return null
    }
    const stored = sessionStorage.getItem(MESSAGES_KEY)
    if (stored) {
      const msgs = JSON.parse(stored)
      // Only restore if there are real messages (not just the default system msg)
      if (Array.isArray(msgs) && msgs.length > 1) return msgs
    }
  } catch { /* corrupt data, start fresh */ }
  return null
}

function saveMessages(msgs: Message[]) {
  try {
    // Don't persist party/system-only conversations
    if (msgs.some(m => m.role === "user" || m.role === "assistant")) {
      sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(msgs))
      sessionStorage.setItem(MESSAGES_TS_KEY, Date.now().toString())
    }
  } catch { /* storage full, ignore */ }
}

// ── Contextual quick actions ─────────────────────────────────────────────
function computeQuickActions(
  messages: Message[],
  incidents: any[],
  autopilot: boolean,
): { label: string; query: string }[] {
  const actions: { label: string; query: string }[] = []
  const open = incidents.filter((i: any) => i.ownership_state !== "resolved")

  // Last response context
  const lastAssistant = [...messages].reverse().find(m => m.role === "assistant")
  const signals = lastAssistant?.meta?.signals || []

  // Incident-driven suggestions
  const criticals = open.filter((i: any) => i.severity === "critical")
  if (criticals.length > 0) {
    actions.push({
      label: `What's wrong? (${criticals.length} critical)`,
      query: `What's happening with the ${criticals.length} critical issue${criticals.length > 1 ? "s" : ""}? Summary and next steps.`,
    })
  }

  if (open.some((i: any) => i.ownership_state === "escalated-human")) {
    actions.push({ label: "What needs me?", query: "What issues have been escalated and need my direct attention?" })
  }

  if (open.some((i: any) => i.source === "worker")) {
    actions.push({ label: "Worker status", query: "What's the status of the GPU workers right now?" })
  }

  if (open.some((i: any) => i.source === "queue" || i.source === "redis")) {
    actions.push({ label: "Queue health", query: "How's the job queue? Any backups or stuck jobs?" })
  }

  // Response-driven suggestions
  if (signals.includes("worker_down") || signals.includes("worker_stuck")) {
    actions.push({ label: "Fix workers", query: "How do I fix the down workers? Walk me through it." })
  }
  if (signals.includes("queue_backed_up")) {
    actions.push({ label: "Clear queue", query: "How do I clear the queue backup? What's causing it?" })
  }

  // Defaults
  if (actions.length === 0) {
    actions.push({ label: "Health check", query: "Give me a full system health check — workers, queue, API, database." })
    actions.push({ label: "Any issues?", query: "Are there any errors or issues right now?" })
  }

  if (actions.length < 4) {
    actions.push({ label: "What happened?", query: "What's happened since I was last here? Anything I should know about?" })
  }

  if (!autopilot && actions.length < 4) {
    actions.push({ label: "Enable autopilot", query: "Should I enable autopilot? What will it do?" })
  }

  if (actions.length < 4) {
    actions.push({ label: "Run diagnostics", query: "Run a diagnostic sweep of all systems and tell me what you find." })
  }

  return actions.slice(0, 4)
}

/** Party message bubble — styled differently with agent portrait + reply arrow */
function PartyBubble({ message, onReply }: { message: Message; onReply?: (agentName: string) => void }) {
  const agent = message.partyAgent
  if (!agent) return null

  return (
    <div className="flex items-start gap-2 animate-in slide-in-from-bottom-2 group/party">
      <div className="shrink-0 mt-0.5">
        <PixelPortrait name={agent.name} color={agent.color} size={24} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold" style={{ color: agent.color }}>
            {agent.name}
          </span>
          {onReply && (
            <button
              onClick={() => onReply(agent.name)}
              className="opacity-0 group-hover/party:opacity-100 transition-opacity flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] text-gray-600 hover:text-blue-400 hover:bg-blue-500/10"
              title={`Reply to @${agent.name}`}
            >
              <Reply size={9} />
            </button>
          )}
        </div>
        <div className="text-sm text-gray-300 leading-relaxed">
          {message.content}
        </div>
      </div>
    </div>
  )
}

/** User message bubble during party — styled like a user joining the conversation */
function PartyUserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end animate-in slide-in-from-bottom-2">
      <div className="max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed bg-accent text-white rounded-2xl rounded-br-sm whitespace-pre-wrap">
        {content}
      </div>
    </div>
  )
}

export default function Chat({ onActivity, prefillInput, onPrefillConsumed, onSwitchTab }: Props) {
  const storedMessages = useRef(loadStoredMessages())
  const [messages, setMessages] = useState<Message[]>(
    storedMessages.current || [
      { role: "system", content: "Pulse is online. Ask me anything about the system, or tell me what to do." },
    ]
  )
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(loadSession)
  const [partyMode, setPartyMode] = useState(false)    // are we in a party?
  const [partyLoading, setPartyLoading] = useState(false) // loading initial party or reply
  const [partyHistory, setPartyHistory] = useState<Array<{ agent_name: string; message: string }>>([])
  const [mentionSuggestions, setMentionSuggestions] = useState<typeof AGENTS[number][]>([])
  const [mentionIndex, setMentionIndex] = useState(0)
  const [autopilot, setAutopilot] = useState(false)
  const [autopilotActions, setAutopilotActions] = useState(0)
  const [liveIncidents, setLiveIncidents] = useState<any[]>([])
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading, partyLoading])

  useEffect(() => { inputRef.current?.focus() }, [])

  // Persist messages to sessionStorage so they survive tab switches
  useEffect(() => { saveMessages(messages) }, [messages])

  // Track last-seen autopilot action count to detect new actions
  const lastSeenActions = useRef(0)
  const lastSeenIncidentStates = useRef<Map<number, string>>(new Map())

  // Poll autopilot status + incidents every 15s — proactive monitoring
  useEffect(() => {
    const poll = () => {
      // Autopilot status
      getAutopilotStatus()
        .then(data => {
          setAutopilot(data.enabled)
          setAutopilotActions(data.actions_taken)

          // Inject new autopilot actions as system messages in the chat
          const currentCount = data.actions_taken || 0
          if (lastSeenActions.current > 0 && currentCount > lastSeenActions.current) {
            const newActions = (data.recent_log || [])
              .filter(e => e.action !== "enabled" && e.action !== "disabled")
              .slice(0, currentCount - lastSeenActions.current)
            for (const entry of newActions.reverse()) {
              const src = entry.source_agent || "system"
              setMessages(prev => [...prev, {
                role: "system",
                content: `[Autopilot] ${entry.action} (${src}): ${entry.detail}`,
              }])
            }
          }
          lastSeenActions.current = currentCount
        })
        .catch(() => {})

      // Incident monitoring — detect new alerts and state changes
      getIncidents()
        .then(data => {
          const incidents = data.incidents || []
          setLiveIncidents(incidents)

          for (const inc of incidents) {
            const prevState = lastSeenIncidentStates.current.get(inc.id)

            if (prevState === undefined && lastSeenIncidentStates.current.size > 0) {
              // New incident appeared (not first load)
              if (inc.severity === "critical" || inc.severity === "warn") {
                setMessages(prev => [...prev, {
                  role: "system",
                  content: `New ${inc.severity} alert: ${inc.message} (#${inc.id})`,
                }])
              }
            } else if (prevState && prevState !== inc.ownership_state) {
              // State changed
              const label =
                inc.ownership_state === "resolved" ? "resolved" :
                inc.ownership_state === "escalated-human" ? "escalated — needs your attention" :
                inc.ownership_state === "autopilot-remediating" ? "being fixed by autopilot" :
                inc.ownership_state
              setMessages(prev => [...prev, {
                role: "system",
                content: `Incident #${inc.id} is now ${label}: ${inc.message}`,
              }])
            }

            lastSeenIncidentStates.current.set(inc.id, inc.ownership_state)
          }
        })
        .catch(() => {})
    }
    poll()
    const id = setInterval(poll, 15000)
    return () => clearInterval(id)
  }, [])

  // Fetch welcome-back message on mount — skip if we restored previous conversation
  useEffect(() => {
    if (storedMessages.current) return
    getWelcome()
      .then(data => {
        if (data.message) {
          setMessages(prev => [...prev, { role: "assistant", content: data.message }])
        }
      })
      .catch(() => {})
  }, [])

  // @mention autocomplete — detect "@" followed by partial name
  const handleInputChange = useCallback((value: string) => {
    setInput(value)
    const atMatch = value.match(/@(\w*)$/)
    if (atMatch) {
      const partial = atMatch[1].toLowerCase()
      const matches = AGENTS.filter(a => a.name.toLowerCase().startsWith(partial))
      setMentionSuggestions(matches)
      setMentionIndex(0)
    } else {
      setMentionSuggestions([])
    }
  }, [])

  const completeMention = useCallback((agentName: string) => {
    // Replace the @partial with @AgentName
    const newInput = input.replace(/@\w*$/, `@${agentName} `)
    setInput(newInput)
    setMentionSuggestions([])
    inputRef.current?.focus()
  }, [input])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setMentionIndex(i => Math.min(i + 1, mentionSuggestions.length - 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setMentionIndex(i => Math.max(i - 1, 0))
      } else if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault()
        completeMention(mentionSuggestions[mentionIndex].name)
      } else if (e.key === "Escape") {
        setMentionSuggestions([])
      }
    }
  }, [mentionSuggestions, mentionIndex, completeMention])

  const sendMessage = async (question: string) => {
    if (!question || loading) return

    setInput("")
    setMentionSuggestions([])
    setMessages(prev => [...prev, { role: "user", content: question }])
    setLoading(true)
    onActivity?.([])

    try {
      const data = await ask(question, sessionId)
      setSessionId(data.session_id)
      saveSession(data.session_id)
      const agents = data.agents_used || []
      const suggestedActions = data.delegation?.suggested_actions || []
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.answer,
        meta: {
          backend: data.backend,
          agents,
          suggestedActions,
          confidence: data.routing_confidence,
          signals: data.delegation?.signals,
          rationale: data.delegation?.rationale,
          evidence: data.routing_evidence,
        },
      }])
      onActivity?.(agents)
    } catch (err: any) {
      // Fall back to incident data instead of showing a dead-end error
      let fallback = `I couldn't complete the analysis (${err.message}).`
      try {
        const snap = await getIncidents()
        const open = (snap.incidents || []).filter((i: any) => i.ownership_state !== "resolved")
        if (open.length > 0) {
          fallback += `\n\nBut here's what the monitoring layer shows — **${open.length} open issue${open.length !== 1 ? "s" : ""}:**\n`
          for (const inc of open.slice(0, 5)) {
            const sev = inc.severity === "critical" ? "CRIT" : inc.severity === "warn" ? "WARN" : "INFO"
            fallback += `- **${sev}** #${inc.id}: ${inc.message}`
            if (inc.ownership_state === "autopilot-remediating") fallback += " *(autopilot fixing)*"
            else if (inc.ownership_state === "escalated-human") fallback += " *(needs you)*"
            fallback += "\n"
          }
          fallback += "\nSwitch to the **Command** tab for full investigation and actions."
        } else {
          fallback += "\n\nThe monitoring layer shows **no open incidents** — systems look healthy from that angle."
        }
      } catch {
        fallback += "\n\nThe monitoring layer is also unreachable — the backend may be down entirely."
      }
      setMessages(prev => [...prev, {
        role: "assistant",
        content: fallback,
        meta: { backend: "error", agents: [] },
      }])
      onActivity?.([])
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  // Drip-feed party messages with delays
  const dripFeed = async (partyMessages: Array<{ agent_id: string; agent_name: string; message: string }>) => {
    for (let i = 0; i < partyMessages.length; i++) {
      const pm = partyMessages[i]
      // Natural pacing — varies like real people typing
      const baseDelay = i === 0 ? 1200 : 2000 + Math.random() * 2500
      await new Promise(r => setTimeout(r, baseDelay))
      setMessages(prev => [...prev, {
        role: "party" as const,
        content: pm.message,
        partyAgent: {
          id: pm.agent_id,
          name: pm.agent_name,
          color: AGENT_COLORS[pm.agent_id] || "#6366f1",
        },
      }])
      setPartyHistory(prev => [...prev, { agent_name: pm.agent_name, message: pm.message }])
    }
    const activeIds = [...new Set(partyMessages.map(m => m.agent_id))]
    onActivity?.(activeIds)
  }

  // Start office party
  const handleParty = useCallback(async () => {
    if (loading || partyLoading || partyMode) return
    setPartyMode(true)
    setPartyLoading(true)
    setPartyHistory([])

    setMessages(prev => [...prev, {
      role: "system",
      content: "The team is taking a break... Office party starting! Jump in anytime — type @AgentName to talk to someone specific.",
    }])

    try {
      const data = await startParty(sessionId)
      await dripFeed(data.messages || [])
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: "system",
        content: "Party got interrupted... something went wrong. Try again later!",
      }])
      setPartyMode(false)
    } finally {
      setPartyLoading(false)
      inputRef.current?.focus()
    }
  }, [loading, partyLoading, partyMode])

  // Reply during party — user chats, agents respond
  const handlePartyReply = useCallback(async (message: string) => {
    if (!message || partyLoading) return

    setInput("")
    setMentionSuggestions([])
    // Show user's message in party context
    setMessages(prev => [...prev, { role: "user", content: message }])
    setPartyLoading(true)

    try {
      const data = await partyReply(message, partyHistory, sessionId)
      await dripFeed(data.messages || [])
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: "system",
        content: "The team got distracted... try saying something else!",
      }])
    } finally {
      setPartyLoading(false)
      inputRef.current?.focus()
    }
  }, [partyLoading, partyHistory])

  // End the party and go back to normal
  const endParty = useCallback(() => {
    setPartyMode(false)
    setPartyHistory([])
    setMessages(prev => [...prev, {
      role: "system",
      content: "Party's over! Back to work. The team will remember this one.",
    }])
    inputRef.current?.focus()
  }, [])

  // Toggle autopilot mode
  const handleAutopilotToggle = useCallback(async () => {
    const newState = !autopilot
    try {
      const result = await toggleAutopilot(newState)
      if (result.error) {
        setMessages(prev => [...prev, {
          role: "system",
          content: `Autopilot toggle encountered a server issue: ${result.error}. Check Pulse logs for details.`,
        }])
        return
      }
      setAutopilot(result.enabled)
      setAutopilotActions(result.actions_taken)
      setMessages(prev => [...prev, {
        role: "system",
        content: result.enabled
          ? "Autopilot engaged. The team is watching the system and will take corrective action if needed. You'll be notified of any actions taken."
          : "Autopilot disengaged. The team is still monitoring but won't take automatic action.",
      }])
    } catch (err) {
      const detail = err instanceof Error ? err.message : "unknown error"
      setMessages(prev => [...prev, {
        role: "system",
        content: `Failed to toggle autopilot (${detail}). Check that Pulse backend is running.`,
      }])
    }
  }, [autopilot])

  // "Draw it out" — generates Mermaid diagrams
  const handleDrawItOut = useCallback((content: string) => {
    // Include party conversation context if we're in or just left party mode
    let contextBlock = content.slice(0, 2000)
    if (partyHistory.length > 0) {
      const partyContext = partyHistory
        .slice(-10)
        .map(m => `${m.agent_name}: ${m.message}`)
        .join("\n")
      contextBlock = `Recent conversation:\n${partyContext}\n\nMessage to visualize:\n${contextBlock}`
    }
    const prompt = `Draw a visual diagram of what you just explained using Mermaid.js syntax. Put the diagram inside a \`\`\`mermaid code block. Use flowchart TD (top-down) or LR (left-right) for architecture/flow diagrams, sequenceDiagram for request flows, or graph for relationships. Use descriptive node labels and clear arrow labels. Keep it clean and readable. Here's what to visualize:\n\n${contextBlock}`
    sendMessage(prompt)
  }, [loading, sessionId, partyHistory])

  // Reply to a message — prefills input with @mention or quote
  const handleReply = useCallback((content: string, agentId?: string) => {
    if (agentId) {
      // Reply to a specific agent — find agent name from ID
      const agent = AGENTS.find(a => a.id === agentId)
      const name = agent?.name || agentId
      setInput(`@${name} `)
    } else {
      // Reply to a general message — quote the first line as context
      const firstLine = content.split("\n")[0].slice(0, 100)
      setInput(`Re: "${firstLine}" — `)
    }
    inputRef.current?.focus()
  }, [])

  // Reply arrow on a party bubble — prefills input with @AgentName
  const handlePartyReplyArrow = useCallback((agentName: string) => {
    setInput(`@${agentName} `)
    inputRef.current?.focus()
  }, [])

  // Save reaction to backend
  const handleReaction = useCallback((content: string, reaction: string) => {
    if (sessionId) {
      saveReaction(sessionId, content.slice(0, 200), reaction).catch(() => {})
    }
  }, [sessionId])

  // Handle prefilled commands from CommandsPanel
  useEffect(() => {
    if (prefillInput) {
      onPrefillConsumed?.()
      sendMessage(prefillInput)
    }
  }, [prefillInput])

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = input.trim()
    if (!trimmed) return
    if (partyMode) {
      handlePartyReply(trimmed)
    } else {
      sendMessage(trimmed)
    }
  }

  const isLoading = loading || partyLoading
  const quickActions = partyMode ? [] : computeQuickActions(messages, liveIncidents, autopilot)

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Party mode banner */}
      {partyMode && !partyLoading && (
        <div className="flex items-center justify-between px-4 py-1.5 bg-amber-500/10 border-b border-amber-500/20">
          <span className="text-xs text-amber-400 font-medium">
            Office Party — chat with the team! Use @name to address someone.
          </span>
          <button
            onClick={endParty}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
          >
            <X size={12} />
            End party
          </button>
        </div>
      )}

      {/* Autopilot banner */}
      {autopilot && !partyMode && (
        <div className="flex items-center justify-between px-4 py-1.5 bg-emerald-500/10 border-b border-emerald-500/20">
          <span className="text-xs text-emerald-400 font-medium flex items-center gap-1.5">
            <Shield size={11} />
            Autopilot — the team is self-guarding
            {autopilotActions > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onSwitchTab?.("status") }}
                className="text-emerald-500/60 ml-1 hover:text-emerald-400 hover:underline transition-colors"
              >
                ({autopilotActions} action{autopilotActions !== 1 ? "s" : ""} taken)
              </button>
            )}
          </span>
          <button
            onClick={handleAutopilotToggle}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
          >
            <X size={12} />
            Disengage
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 flex flex-col gap-3 overscroll-contain">
        {messages.map((msg, i) => (
          msg.role === "party" ? (
            <PartyBubble key={i} message={msg} onReply={handlePartyReplyArrow} />
          ) : (
            <MessageBubble
              key={i}
              message={msg}
              onDrawItOut={handleDrawItOut}
              onReaction={handleReaction}
              onReply={handleReply}
            />
          )
        ))}
        {isLoading && (
          <div className="flex items-center gap-2 py-2">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <span className="text-gray-500 text-sm">
              {partyLoading ? "The team is chatting..." : "Commander is investigating..."}
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick action chips — contextual suggestions */}
      {!isLoading && quickActions.length > 0 && !partyMode && (
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide px-3 py-1.5 border-t border-border/50">
          {quickActions.map((qa, i) => (
            <button
              key={i}
              onClick={() => sendMessage(qa.query)}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs bg-surface/50 text-gray-400 hover:text-gray-200 hover:bg-surface/80 border border-border/30 hover:border-border/60 transition-colors whitespace-nowrap"
            >
              {qa.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        {/* @mention autocomplete dropdown */}
        {mentionSuggestions.length > 0 && (
          <div className="absolute bottom-full left-3 mb-1 bg-surface border border-border rounded-lg shadow-lg overflow-hidden z-10">
            {mentionSuggestions.map((agent, i) => (
              <button
                key={agent.id}
                onClick={() => completeMention(agent.name)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-card transition-colors ${
                  i === mentionIndex ? "bg-card" : ""
                }`}
              >
                <PixelPortrait name={agent.name} color={agent.color} size={16} />
                <span style={{ color: agent.color }} className="font-medium">{agent.name}</span>
                <span className="text-gray-500 text-xs ml-1">
                  {agent.id === "api" ? "API" : agent.id === "worker" ? "GPU" : agent.id === "redis" ? "Redis" :
                   agent.id === "db" ? "DB" : agent.id === "railway" ? "Platform" : agent.id === "ui" ? "UI" :
                   agent.id === "ops" ? "Ops" : agent.id === "moderator" ? "Mod" : "Labs"}
                </span>
              </button>
            ))}
          </div>
        )}
        <form onSubmit={send} className="flex gap-2 p-3 border-t border-border bg-surface safe-bottom">
          <Button
            type="button"
            onClick={partyMode ? endParty : handleParty}
            disabled={isLoading}
            size="icon"
            variant="ghost"
            title={partyMode ? "End the party" : "Office party — agents chat with each other!"}
            className={`shrink-0 ${partyMode
              ? "text-amber-400 hover:text-red-400 hover:bg-red-500/10"
              : "text-gray-500 hover:text-amber-400 hover:bg-amber-500/10"
            }`}
          >
            {partyMode ? <X size={16} /> : <PartyPopper size={16} />}
          </Button>
          <Button
            type="button"
            onClick={handleAutopilotToggle}
            disabled={isLoading || partyMode}
            size="icon"
            variant="ghost"
            title={autopilot
              ? `Autopilot ON — ${autopilotActions} actions taken. Click to disengage.`
              : "Autopilot — let the team self-guard and take corrective action"
            }
            className={`shrink-0 relative ${autopilot
              ? "text-emerald-400 hover:text-red-400 hover:bg-red-500/10"
              : "text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10"
            }`}
          >
            <Shield size={16} />
            {autopilot && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            )}
          </Button>
          <Input
            ref={inputRef}
            placeholder={partyMode ? "Say something to the team... (@name to target)" : "Ask Pulse anything or give a command..."}
            value={input}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="flex-1"
          />
          <Button type="submit" disabled={isLoading || !input.trim()} size="icon">
            <Send size={16} />
          </Button>
        </form>
      </div>
    </div>
  )
}
